type FFmpegInstance = import('@ffmpeg/ffmpeg').FFmpeg;

const FFMPEG_CORE_VERSION = '0.12.6';
const FFMPEG_CORE_BASE_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/esm`;

/** Same-origin URLs — Web Workers cannot load cross-origin scripts. */
function getFfmpegAssetUrl(relativePath: string): string {
  return `${window.location.origin}/ffmpeg/${relativePath}`;
}

export type VideoTrimSegment = {
  start: number;
  end: number;
};

type FfmpegDeps = {
  FFmpeg: typeof import('@ffmpeg/ffmpeg').FFmpeg;
  fetchFile: (data: File | Blob | string) => Promise<Uint8Array>;
  toBlobURL: (url: string, mimeType: string) => Promise<string>;
};

let ffmpegInstance: FFmpegInstance | null = null;
let loadPromise: Promise<FFmpegInstance> | null = null;
let depsPromise: Promise<FfmpegDeps> | null = null;

/** Loads an ESM module from a URL without Webpack trying to bundle it. */
function importEsmModule<T>(url: string): Promise<T> {
  // eslint-disable-next-line no-new-func
  const dynamicImport = new Function('url', 'return import(url)') as (moduleUrl: string) => Promise<T>;
  return dynamicImport(url);
}

/** Dynamically imports FFmpeg.wasm from the app origin (worker must be same-origin). */
async function loadFfmpegDeps(): Promise<FfmpegDeps> {
  if (!depsPromise) {
    depsPromise = Promise.all([
      importEsmModule<typeof import('@ffmpeg/ffmpeg')>(getFfmpegAssetUrl('esm/index.js')),
      importEsmModule<typeof import('@ffmpeg/util')>(getFfmpegAssetUrl('util/index.js')),
    ]).then(([ffmpegModule, utilModule]) => ({
      FFmpeg: ffmpegModule.FFmpeg,
      fetchFile: utilModule.fetchFile,
      toBlobURL: utilModule.toBlobURL,
    }));
  }
  return depsPromise;
}

/** Re-encodes a single time range with accurate output seeking (no keyframe overlap). */
async function runTrimCommand(
  ffmpeg: FFmpegInstance,
  inputName: string,
  outputName: string,
  startSeconds: number,
  durationSeconds: number,
  includeAudio: boolean,
): Promise<void> {
  const endSeconds = startSeconds + durationSeconds;
  const args = [
    '-i',
    inputName,
    '-ss',
    String(startSeconds),
    '-to',
    String(endSeconds),
    '-c:v',
    'libx264',
    '-preset',
    'ultrafast',
    '-pix_fmt',
    'yuv420p',
    '-avoid_negative_ts',
    'make_zero',
    '-movflags',
    '+faststart',
  ];

  if (includeAudio) {
    args.push('-c:a', 'aac');
  } else {
    args.push('-an');
  }

  args.push(outputName);

  const exitCode = await ffmpeg.exec(args);
  if (exitCode !== 0) {
    throw new Error('FFmpeg trim failed');
  }
}

/** Builds a trim+concat filter graph so segment boundaries stay frame-accurate. */
function buildTrimConcatFilter(segments: VideoTrimSegment[], includeAudio: boolean): string {
  const filterParts: string[] = [];
  const concatInputs: string[] = [];

  segments.forEach((segment, index) => {
    const videoLabel = `v${index}`;
    filterParts.push(
      `[0:v]trim=start=${segment.start}:end=${segment.end},setpts=PTS-STARTPTS[${videoLabel}]`,
    );
    concatInputs.push(`[${videoLabel}]`);

    if (includeAudio) {
      const audioLabel = `a${index}`;
      filterParts.push(
        `[0:a]atrim=start=${segment.start}:end=${segment.end},asetpts=PTS-STARTPTS[${audioLabel}]`,
      );
      concatInputs.push(`[${audioLabel}]`);
    }
  });

  const segmentCount = segments.length;
  if (includeAudio) {
    filterParts.push(`${concatInputs.join('')}concat=n=${segmentCount}:v=1:a=1[outv][outa]`);
  } else {
    filterParts.push(`${concatInputs.join('')}concat=n=${segmentCount}:v=1:a=0[outv]`);
  }

  return filterParts.join(';');
}

/** Trims and joins multiple kept segments in one pass without overlap at cut points. */
async function runFilterComplexTrim(
  ffmpeg: FFmpegInstance,
  inputName: string,
  outputName: string,
  segments: VideoTrimSegment[],
  includeAudio: boolean,
): Promise<void> {
  const args = [
    '-i',
    inputName,
    '-filter_complex',
    buildTrimConcatFilter(segments, includeAudio),
    '-map',
    '[outv]',
  ];

  if (includeAudio) {
    args.push('-map', '[outa]', '-c:a', 'aac');
  }

  args.push(
    '-c:v',
    'libx264',
    '-preset',
    'ultrafast',
    '-pix_fmt',
    'yuv420p',
    '-movflags',
    '+faststart',
    outputName,
  );

  const exitCode = await ffmpeg.exec(args);
  if (exitCode !== 0) {
    throw new Error('FFmpeg trim failed');
  }
}

async function exportTrimmedVideo(
  ffmpeg: FFmpegInstance,
  inputName: string,
  outputName: string,
  segments: VideoTrimSegment[],
): Promise<void> {
  if (segments.length === 1) {
    const segment = segments[0];
    try {
      await runTrimCommand(
        ffmpeg,
        inputName,
        outputName,
        segment.start,
        segment.end - segment.start,
        true,
      );
      return;
    } catch {
      await runTrimCommand(
        ffmpeg,
        inputName,
        outputName,
        segment.start,
        segment.end - segment.start,
        false,
      );
      return;
    }
  }

  try {
    await runFilterComplexTrim(ffmpeg, inputName, outputName, segments, true);
  } catch {
    await runFilterComplexTrim(ffmpeg, inputName, outputName, segments, false);
  }
}

function getFileExtension(fileName: string): string {
  const match = fileName.match(/(\.[^.]+)$/);
  return match ? match[1].toLowerCase() : '.mp4';
}

function normalizeSegments(segments: VideoTrimSegment[]): VideoTrimSegment[] {
  return segments
    .map((segment) => ({
      start: Math.max(0, segment.start),
      end: Math.max(segment.start + 0.1, segment.end),
    }))
    .sort((a, b) => a.start - b.start);
}

/** Lazily loads FFmpeg.wasm once and reuses the instance for subsequent trims. */
export async function getFFmpeg(onLog?: (message: string) => void): Promise<FFmpegInstance> {
  if (ffmpegInstance?.loaded) {
    return ffmpegInstance;
  }

  if (!loadPromise) {
    loadPromise = (async () => {
      const { FFmpeg, toBlobURL } = await loadFfmpegDeps();
      const ffmpeg = new FFmpeg();
      if (onLog) {
        ffmpeg.on('log', ({ message }: { message: string }) => onLog(message));
      }
      await ffmpeg.load({
        classWorkerURL: getFfmpegAssetUrl('esm/worker.js'),
        coreURL: await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    })();
  }

  return loadPromise;
}

/**
 * Keeps only the listed segments (in order) and concatenates them into one MP4.
 * Gaps between segments are removed from the output.
 */
export async function trimVideoSegments(
  file: File,
  segments: VideoTrimSegment[],
  onLog?: (message: string) => void,
  onProgress?: (message: string) => void,
): Promise<File> {
  const normalized = normalizeSegments(segments);
  if (!normalized.length) {
    throw new Error('Add at least one segment to keep.');
  }

  const { fetchFile } = await loadFfmpegDeps();
  const ffmpeg = await getFFmpeg(onLog);
  const inputName = `input${getFileExtension(file.name)}`;
  const outputName = 'output.mp4';

  onProgress?.(
    normalized.length === 1 ? 'Processing video…' : `Processing ${normalized.length} clips…`,
  );
  await ffmpeg.writeFile(inputName, await fetchFile(file));
  await exportTrimmedVideo(ffmpeg, inputName, outputName, normalized);

  const data = await ffmpeg.readFile(outputName);
  await ffmpeg.deleteFile(inputName);
  await ffmpeg.deleteFile(outputName);

  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  const blob = new Blob([bytes], { type: 'video/mp4' });
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'video';
  return new File([blob], `${baseName}-trimmed.mp4`, { type: 'video/mp4' });
}

/** Trims a video file to the given [start, end] range and returns an MP4 File. */
export async function trimVideoFile(
  file: File,
  startSeconds: number,
  endSeconds: number,
  onLog?: (message: string) => void,
): Promise<File> {
  return trimVideoSegments(file, [{ start: startSeconds, end: endSeconds }], onLog);
}

/** Total duration of all keep-segments combined. */
export function getSegmentsTotalDuration(segments: VideoTrimSegment[]): number {
  return segments.reduce((total, segment) => total + Math.max(0, segment.end - segment.start), 0);
}

/** Returns true when two time ranges overlap. */
export function segmentsOverlap(a: VideoTrimSegment, b: VideoTrimSegment): boolean {
  return a.start < b.end - 0.05 && b.start < a.end - 0.05;
}

/** Formats seconds as m:ss for trim UI labels. */
export function formatVideoTimeLabel(seconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  }
  return `0:${String(secs).padStart(2, '0')}`;
}

/** Formats seconds with one decimal for compact labels (e.g. 3.5s). */
export function formatVideoTimePrecise(seconds: number): string {
  const rounded = Math.round(seconds * 10) / 10;
  const mins = Math.floor(rounded / 60);
  const secs = Math.round((rounded % 60) * 10) / 10;
  if (mins > 0) {
    return `${mins}:${String(Math.floor(secs)).padStart(2, '0')}`;
  }
  return `${secs}s`;
}
